import { type RefObject, useCallback, useEffect } from 'react';

/**
 * Hook for tracking the height of the header and updating the CSS variable --header-height
 * @param headerRef - ref on the header element
 * @param dependencies - array of dependencies, when they change, the height needs to be updated (e.g. pathname)
 * @param isHidden - optional flag indicating whether the header is hidden (if true, sets 0px)
 */
export const useHeaderHeight = (
  headerRef: RefObject<HTMLElement>,
  dependencies: unknown[] = [],
  isHidden?: boolean,
) => {
  const updateHeaderHeight = useCallback(() => {
    if (isHidden) {
      document.body.style.setProperty('--header-height', '0px');
      return;
    }

    const headerElement = headerRef.current;
    if (!headerElement) return;
    const height = headerElement.offsetHeight;
    document.body.style.setProperty('--header-height', `${height}px`);
  }, [headerRef, isHidden]);

  useEffect(() => {
    if (isHidden) {
      updateHeaderHeight();
      return;
    }

    const headerElement = headerRef.current;
    if (!headerElement) {
      const timeoutId = setTimeout(() => {
        updateHeaderHeight();
      }, 0);
      return () => clearTimeout(timeoutId);
    }

    const resizeObserver = new ResizeObserver(() => updateHeaderHeight());

    resizeObserver.observe(headerElement);

    updateHeaderHeight();

    return () => resizeObserver.disconnect();
  }, [updateHeaderHeight, isHidden, ...dependencies]);
};
